// Choose a day and a block for a task (spec T2). Work blocks are never offered; optional blocks are (T2 AC5).
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { pickTemplateId, sortBlocks } from "@shared/rhythm";
import { addDays, displayDate, displayTime, planningDate } from "@shared/time";
import { db } from "../lib/db";
import type { PlanTarget } from "../lib/tasks";
import { cn } from "./ui";

interface Choice {
  key: string;
  label: string;
  time: string | null;
  target: PlanTarget;
}

function useBlockChoices(date: string): Choice[] | undefined {
  return useLiveQuery(async () => {
    const plan = await db.day_plans
      .where("plan_date")
      .equals(date)
      .filter((p) => !p.deleted_at)
      .first();
    const none: Choice = { key: "none", label: "Ergens op die dag", time: null, target: { date } };
    if (plan) {
      const blocks = await db.day_blocks
        .where("day_plan_id")
        .equals(plan.id)
        .filter((b) => !b.deleted_at && b.slot_kind !== "work")
        .toArray();
      return [
        none,
        ...sortBlocks(blocks).map((b) => ({ key: b.id, label: b.title, time: b.start_local, target: { date, dayBlockId: b.id } })),
      ];
    }
    const [templates, rules, overrides] = await Promise.all([
      db.day_templates.toArray(),
      db.day_template_rules.toArray(),
      db.day_overrides.toArray(),
    ]);
    const templateId = pickTemplateId(date, templates, rules, overrides);
    const blocks = templateId
      ? await db.day_template_blocks
          .where("template_id")
          .equals(templateId)
          .filter((b) => !b.deleted_at && b.slot_kind !== "work")
          .toArray()
      : [];
    return [
      none,
      ...sortBlocks(blocks).map((b) => ({ key: b.id, label: b.title, time: b.start_local, target: { date, templateBlockId: b.id } })),
    ];
  }, [date]);
}

export function PlanPicker({ dayStart, onPick }: { dayStart: number; onPick: (target: PlanTarget) => void }) {
  const today = planningDate(new Date(), dayStart);
  const [date, setDate] = useState(today);
  const choices = useBlockChoices(date);
  const tomorrow = addDays(today, 1);
  const dayButton = (d: string, label: string) => (
    <button
      type="button"
      aria-pressed={date === d}
      onClick={() => setDate(d)}
      className={cn(
        "min-h-12 flex-1 rounded-xl border-2 px-3 font-semibold",
        date === d ? "border-pine bg-pine text-paper" : "border-mist",
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        {dayButton(today, "Vandaag")}
        {dayButton(tomorrow, "Morgen")}
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-ink-soft">Andere dag</span>
        <input
          type="date"
          min={today}
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
        />
      </label>
      <p className="font-semibold">Kies een blok op {displayDate(date)}</p>
      <ul className="flex flex-col gap-1">
        {(choices ?? []).map((c) => (
          <li key={c.key}>
            <button
              type="button"
              onClick={() => onPick(c.target)}
              className="flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border-2 border-mist px-3 text-left hover:border-pine"
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">{c.label}</span>
              {c.time && <span className="text-sm text-ink-soft tabular">{displayTime(c.time)}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
